/**
 * Role Play Store
 *
 * Zustand store for role play scenario slots and premade templates.
 * Slots are persistent per-user objects that point at a `roleplay_runner`
 * ConversationSession. Templates are curated scenarios seeded per course
 * that can be started directly without a generator chat.
 *
 * @see docs/specs/mobile/roleplay.mobile.md
 * @see docs/specs/api/roleplay.api.md
 */

import { create } from 'zustand';
import { api } from '../services/api';
import { createLogger } from '../utils/logger';
import type { StoreErrorInfo } from '../generated/api/endpoint-errors';
import type {
  RolePlaySlotSummary,
  RolePlaySlotDetail,
  RolePlayTemplateSummary,
  RolePlayVocabularyItem,
  RolePlayPhraseItem,
  RolePlayCollectionSummary,
  RolePlayCatalogVocab,
  RolePlayCatalogExpression,
} from '../generated/api/http-contracts';
import { useUserPreferencesStore } from './userPreferencesStore';
import limits from '../generated/shared/limits.json';

const log = createLogger('RolePlayStore');

/**
 * Convert the user's hyphenated target locale (e.g. `en-US`) to the
 * underscored variant string the catalog API expects (`en_US`). Returns
 * `undefined` when no target locale is set so the catalog falls back to
 * "show all" rather than filtering on a bogus value.
 */
function currentTargetVariant(): string | undefined {
  const targetLocale = useUserPreferencesStore.getState().targetLocale;
  if (!targetLocale) return undefined;
  return targetLocale.replace('-', '_');
}

/** Slot shape in the store — summary fields required, scenarioDetails filled after detail fetch. */
export interface RolePlaySlot extends RolePlaySlotSummary {
  scenarioDetails?: Record<string, string>;
}

/** Template shape in the store — detail fields filled after detail fetch. */
export interface RolePlayTemplate extends RolePlayTemplateSummary {
  scenarioDetails?: Record<string, string>;
  vocabulary?: RolePlayVocabularyItem[];
  phrases?: RolePlayPhraseItem[];
  /** Phase 4: relation-backed catalog fields (preferred over `vocabulary`/`phrases`). */
  catalogVocab?: RolePlayCatalogVocab[];
  catalogExpressions?: RolePlayCatalogExpression[];
  collectionSlug?: string;
  difficulty?: number;
}

/** Collection summary used by the catalog grid. */
export type RolePlayCollection = RolePlayCollectionSummary;

// Re-export types used by screens
export type { RolePlayVocabularyItem, RolePlayPhraseItem, RolePlayCatalogVocab, RolePlayCatalogExpression };

interface RolePlayState {
  // Slot state
  slots: RolePlaySlot[];
  cap: number;
  loading: boolean;
  mutating: boolean;
  errorInfo: StoreErrorInfo | null;

  // Template state
  templates: RolePlayTemplate[];
  templatesLoading: boolean;
  /** Error from the detail FETCH (GET /templates/:id). */
  templateErrorInfo: StoreErrorInfo | null;
  /**
   * Error from the START mutation (POST /templates/:id/start). Kept separate
   * from `templateErrorInfo` so a background detail re-fetch (which clears
   * `templateErrorInfo`) can't wipe a start failure the user still needs to
   * see.
   */
  templateStartErrorInfo: StoreErrorInfo | null;
  templatesCourseId: string | null;

  // Collection state (Phase 4)
  collections: RolePlayCollection[];
  collectionsLoading: boolean;
  collectionsErrorInfo: StoreErrorInfo | null;
  collectionsCourseId: string | null;

  // Search state (Phase 4) — populated by searchTemplates; consumed by
  // the search results list. Cleared when the query goes empty.
  searchResults: RolePlayTemplate[];
  searchLoading: boolean;
  searchErrorInfo: StoreErrorInfo | null;
  searchQuery: string;

  // Slot actions
  loadSlots: () => Promise<void>;
  loadSlotDetail: (id: string) => Promise<void>;
  restartSlot: (id: string) => Promise<string | null>;
  removeSlot: (id: string) => Promise<boolean>;
  clearError: () => void;

  // Template actions
  loadTemplates: (courseId: string) => Promise<void>;
  loadTemplateDetail: (templateId: string) => Promise<void>;
  startFromTemplate: (templateId: string) => Promise<string | null>;
  clearTemplateError: () => void;

  // Collection actions
  loadCollections: (courseId: string) => Promise<void>;
  loadCollectionDetail: (courseId: string, slug: string) => Promise<void>;
  clearCollectionsError: () => void;

  /**
   * Reset all role-play state to initial (E2E affordance for the
   * `dev.clear-roleplay` intent — forces a fresh fetch of slots /
   * collections / templates without a JS reload or re-auth). Mirrors
   * `useMessageStore.reset()`.
   */
  reset: () => void;

  // Search actions
  searchTemplates: (
    courseId: string,
    args: { q?: string; difficulty?: number[]; completed?: 'true' | 'false' },
  ) => Promise<void>;
  clearSearch: () => void;
}

function mergeSlot(slots: RolePlaySlot[], updated: RolePlaySlot): RolePlaySlot[] {
  const idx = slots.findIndex((s) => s.id === updated.id);
  if (idx === -1) return [updated, ...slots];
  const copy = slots.slice();
  // Preserve scenarioDetails from the new payload if present, otherwise keep existing
  copy[idx] = { ...copy[idx], ...updated };
  return copy;
}

function toSlot(raw: RolePlaySlotDetail | RolePlaySlotSummary): RolePlaySlot {
  return raw as RolePlaySlot;
}

export const useRolePlayStore = create<RolePlayState>((set, get) => ({
  slots: [],
  cap: limits.roleplaySlotCap,
  loading: false,
  mutating: false,
  errorInfo: null,

  templates: [],
  templatesLoading: false,
  templateErrorInfo: null,
  templateStartErrorInfo: null,
  templatesCourseId: null,

  collections: [],
  collectionsLoading: false,
  collectionsErrorInfo: null,
  collectionsCourseId: null,

  searchResults: [],
  searchLoading: false,
  searchErrorInfo: null,
  searchQuery: '',

  loadSlots: async () => {
    set({ loading: true, errorInfo: null });
    const result = await api.listRolePlaySlots();
    if (result.ok) {
      set({ slots: result.data.slots.map(toSlot), cap: result.data.cap, loading: false });
    } else {
      log.warn('Failed to load role play slots', { kind: result.kind });
      set({ loading: false, errorInfo: result });
    }
  },

  loadSlotDetail: async (id: string) => {
    set({ errorInfo: null });
    const result = await api.getRolePlaySlot(id);
    if (result.ok) {
      const detail = toSlot(result.data.slot);
      set({ slots: mergeSlot(get().slots, detail) });
    } else {
      log.warn('Failed to load slot detail', { kind: result.kind, id });
      set({ errorInfo: result });
    }
  },

  restartSlot: async (id: string) => {
    if (get().mutating) return null;
    set({ mutating: true, errorInfo: null });
    const result = await api.restartRolePlaySlot(id);
    if (result.ok) {
      const updated = toSlot(result.data.slot);
      set({ slots: mergeSlot(get().slots, updated), mutating: false });
      return updated.conversationId;
    }
    log.warn('Failed to restart slot', { kind: result.kind, id });
    set({ mutating: false, errorInfo: result });
    return null;
  },

  removeSlot: async (id: string) => {
    if (get().mutating) return false;
    set({ mutating: true, errorInfo: null });
    const result = await api.deleteRolePlaySlot(id);
    if (result.ok) {
      set({ slots: get().slots.filter((s) => s.id !== id), mutating: false });
      return true;
    }
    log.warn('Failed to remove slot', { kind: result.kind, id });
    set({ mutating: false, errorInfo: result });
    return false;
  },

  clearError: () => set({ errorInfo: null }),

  // ── Template actions ──────────────────────────────────────────

  loadTemplates: async (courseId: string) => {
    // Skip if already loaded for this course (static seeded data)
    if (get().templatesCourseId === courseId && get().templates.length > 0) return;

    set({ templatesLoading: true, templateErrorInfo: null });
    const result = await api.listRolePlayTemplates({ params: { courseId } });
    if (result.ok) {
      set({ templates: result.data.templates as RolePlayTemplate[], templatesLoading: false, templatesCourseId: courseId });
    } else {
      log.warn('Failed to load templates', { kind: result.kind, courseId });
      set({ templatesLoading: false, templateErrorInfo: result });
    }
  },

  loadTemplateDetail: async (templateId: string) => {
    set({ templateErrorInfo: null });
    // Resolve the course id. Templates loaded via `loadTemplates` stamp
    // `templatesCourseId`, but templates merged from a collection
    // (`loadCollectionDetail`, the drill-down path) don't — so fall back to
    // the active course. Firing the request with a null courseId fails the
    // backend's required-param validation (VALIDATION_FAILED → the
    // "Please check your input and try again" banner).
    const courseId =
      get().templatesCourseId ?? useUserPreferencesStore.getState().activeCourseId;
    if (!courseId) {
      log.warn('loadTemplateDetail skipped — no course id available', { templateId });
      return;
    }
    const result = await api.getRolePlayTemplate(templateId, { params: { courseId } });
    if (result.ok) {
      const detail = result.data.template;
      // Phase 4: also populate catalog-backed fields when present. Old
      // templates without join rows return empty arrays; the preview
      // screen falls back to the legacy `vocabulary` / `phrases` arrays
      // in that case.
      const merged: Partial<RolePlayTemplate> = {
        scenarioDetails: detail.scenarioDetails,
        vocabulary: detail.vocabulary,
        phrases: detail.phrases,
        catalogVocab: detail.catalogVocab ?? [],
        catalogExpressions: detail.catalogExpressions ?? [],
      };
      if (detail.collectionSlug) merged.collectionSlug = detail.collectionSlug;
      if (typeof detail.difficulty === 'number') merged.difficulty = detail.difficulty;
      set({
        templates: get().templates.map((t) =>
          t.id === templateId ? { ...t, ...merged } : t,
        ),
      });
    } else {
      log.warn('Failed to load template detail', { kind: result.kind, templateId });
      set({ templateErrorInfo: result });
    }
  },

  startFromTemplate: async (templateId: string) => {
    if (get().mutating) return null;
    set({ mutating: true, errorInfo: null, templateStartErrorInfo: null });
    const result = await api.startRolePlayTemplate(templateId);
    if (result.ok) {
      const slot = toSlot(result.data.slot);
      set({ slots: mergeSlot(get().slots, slot), mutating: false });
      return slot.conversationId;
    }
    log.warn('Failed to start from template', { kind: result.kind, templateId });
    // Start failure goes to its OWN field so a focus-triggered detail
    // re-fetch (which nulls `templateErrorInfo`) can't wipe it.
    set({ mutating: false, templateStartErrorInfo: result });
    return null;
  },

  // Clears BOTH template-banner errors (detail-fetch + start) — used by the
  // preview before a fresh start attempt.
  clearTemplateError: () => set({ templateErrorInfo: null, templateStartErrorInfo: null }),

  reset: () =>
    set({
      slots: [],
      cap: limits.roleplaySlotCap,
      loading: false,
      mutating: false,
      errorInfo: null,
      templates: [],
      templatesLoading: false,
      templateErrorInfo: null,
      templateStartErrorInfo: null,
      templatesCourseId: null,
      collections: [],
      collectionsLoading: false,
      collectionsErrorInfo: null,
      collectionsCourseId: null,
      searchResults: [],
      searchLoading: false,
      searchErrorInfo: null,
      searchQuery: '',
    }),

  // ── Collection actions (Phase 4) ────────────────────────────

  loadCollections: async (courseId: string) => {
    set({ collectionsLoading: true, collectionsErrorInfo: null });
    const variant = currentTargetVariant();
    const params: Record<string, string> = { courseId };
    if (variant) params.variant = variant;
    const result = await api.listRolePlayCollections({ params });
    if (result.ok) {
      set({
        collections: result.data.collections,
        collectionsLoading: false,
        collectionsCourseId: courseId,
      });
    } else {
      log.warn('Failed to load collections', { kind: result.kind, courseId });
      set({ collectionsLoading: false, collectionsErrorInfo: result });
    }
  },

  loadCollectionDetail: async (courseId: string, slug: string) => {
    set({ collectionsErrorInfo: null });
    const variant = currentTargetVariant();
    const params: Record<string, string> = { courseId };
    if (variant) params.variant = variant;
    const result = await api.getRolePlayCollection(slug, { params });
    if (result.ok) {
      // Merge the situations into the templates store so the preview
      // screen can resolve them by id without a separate fetch.
      //
      // Stamp `collectionSlug: slug` on each: these situations were fetched
      // BY this collection's slug, so we know which collection they belong to
      // — but the API field is optional and the collection-detail response may
      // omit it. RolePlayCollectionScreen filters by `collectionSlug === slug`,
      // so without this stamp the list filters to empty and the screen spins
      // forever even though the fetch succeeded.
      const incoming = result.data.collection.templates as RolePlayTemplate[];
      const existing = get().templates;
      const byId = new Map(existing.map((t) => [t.id, t]));
      for (const t of incoming) byId.set(t.id, { ...byId.get(t.id), ...t, collectionSlug: slug });
      set({ templates: Array.from(byId.values()) });
    } else {
      log.warn('Failed to load collection detail', { kind: result.kind, slug });
      set({ collectionsErrorInfo: result });
    }
  },

  clearCollectionsError: () => set({ collectionsErrorInfo: null }),

  // ── Search actions (Phase 4) ────────────────────────────────

  searchTemplates: async (courseId, args) => {
    const q = (args.q ?? '').trim();
    set({ searchLoading: true, searchErrorInfo: null, searchQuery: q });
    const variant = currentTargetVariant();
    const params: Record<string, string> = { courseId };
    if (q.length > 0) params.q = q;
    if (args.difficulty && args.difficulty.length > 0) {
      params.difficulty = args.difficulty.join(',');
    }
    if (args.completed) params.completed = args.completed;
    if (variant) params.variant = variant;
    const result = await api.searchRolePlayTemplates({ params });
    if (result.ok) {
      set({ searchResults: result.data.templates as RolePlayTemplate[], searchLoading: false });
    } else {
      log.warn('Failed to search templates', { kind: result.kind, courseId, q });
      set({ searchLoading: false, searchErrorInfo: result });
    }
  },

  clearSearch: () => {
    // Idempotent: when search state is already cleared, skip the `set` so we
    // don't hand out fresh `[]`/`''` references that re-render subscribers for
    // no reason (defense-in-depth against a setState-in-render loop — the
    // collections screen calls this from an effect on the empty/no-filter path).
    const s = get();
    if (
      s.searchResults.length === 0 &&
      s.searchLoading === false &&
      s.searchErrorInfo === null &&
      s.searchQuery === ''
    ) {
      return;
    }
    set({ searchResults: [], searchLoading: false, searchErrorInfo: null, searchQuery: '' });
  },
}));

// ── Individual selectors (no combined objects) ─────────────────────────

// ── Slot selectors ──────────────────────────────────────────────
export const selectRolePlaySlots = (s: RolePlayState) => s.slots;
export const selectRolePlayCap = (s: RolePlayState) => s.cap;
export const selectRolePlayLoading = (s: RolePlayState) => s.loading;
export const selectRolePlayMutating = (s: RolePlayState) => s.mutating;
export const selectRolePlayErrorInfo = (s: RolePlayState) => s.errorInfo;
export const selectLoadSlots = (s: RolePlayState) => s.loadSlots;
export const selectLoadSlotDetail = (s: RolePlayState) => s.loadSlotDetail;
export const selectRestartSlot = (s: RolePlayState) => s.restartSlot;
export const selectRemoveSlot = (s: RolePlayState) => s.removeSlot;
export const selectClearRolePlayError = (s: RolePlayState) => s.clearError;

// ── Template selectors ──────────────────────────────────────────
export const selectRolePlayTemplates = (s: RolePlayState) => s.templates;
export const selectRolePlayTemplatesLoading = (s: RolePlayState) => s.templatesLoading;
export const selectRolePlayTemplateErrorInfo = (s: RolePlayState) => s.templateErrorInfo;
export const selectRolePlayTemplateStartErrorInfo = (s: RolePlayState) => s.templateStartErrorInfo;
export const selectLoadTemplates = (s: RolePlayState) => s.loadTemplates;
export const selectLoadTemplateDetail = (s: RolePlayState) => s.loadTemplateDetail;
export const selectStartFromTemplate = (s: RolePlayState) => s.startFromTemplate;
export const selectClearTemplateError = (s: RolePlayState) => s.clearTemplateError;
export const selectRolePlayTemplateById = (id: string) => (s: RolePlayState) =>
  s.templates.find((t) => t.id === id) ?? null;
export const selectRolePlayTemplatesByCategory = (s: RolePlayState) => {
  const grouped: Record<string, RolePlayTemplate[]> = {};
  for (const t of s.templates) {
    (grouped[t.category] ??= []).push(t);
  }
  return grouped;
};

// ── Collection selectors (Phase 4) ──────────────────────────────
export const selectRolePlayCollections = (s: RolePlayState) => s.collections;
export const selectRolePlayCollectionsLoading = (s: RolePlayState) => s.collectionsLoading;
export const selectRolePlayCollectionsErrorInfo = (s: RolePlayState) => s.collectionsErrorInfo;
export const selectLoadCollections = (s: RolePlayState) => s.loadCollections;
export const selectLoadCollectionDetail = (s: RolePlayState) => s.loadCollectionDetail;
export const selectClearCollectionsError = (s: RolePlayState) => s.clearCollectionsError;
export const selectRolePlayCollectionBySlug = (slug: string) => (s: RolePlayState) =>
  s.collections.find((c) => c.slug === slug) ?? null;
// NOTE: there is intentionally no `selectTemplatesByCollectionSlug` that
// returns `s.templates.filter(...)` — a selector returning a fresh array each
// call breaks Zustand v5's `useSyncExternalStore` snapshot caching (infinite
// re-render). Select the stable `selectRolePlayTemplates` and filter in a
// component `useMemo` instead (see RolePlayCollectionScreen).

// ── Search selectors (Phase 4) ──────────────────────────────────
export const selectRolePlaySearchResults = (s: RolePlayState) => s.searchResults;
export const selectRolePlaySearchLoading = (s: RolePlayState) => s.searchLoading;
export const selectRolePlaySearchErrorInfo = (s: RolePlayState) => s.searchErrorInfo;
export const selectRolePlaySearchQuery = (s: RolePlayState) => s.searchQuery;
export const selectSearchTemplates = (s: RolePlayState) => s.searchTemplates;
export const selectClearSearch = (s: RolePlayState) => s.clearSearch;
